import { useEffect, useRef, useState } from 'react';
import { Crown, Send, User, Mail, ArrowRight } from 'lucide-react';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MarkdownLite } from './MarkdownLite';
import { useChatWidgetStore } from './ChatWidgetStore';
import { useDominaAgent, type AgentMessage } from './useDominaAgent';
import { QUICK_STARTS, type AgentAction } from '../../lib/agentBrain';
import { api } from '../../lib/api';
import { useCurrentWorkspace } from '../../lib/workspace';
import { useStartDm } from '../../lib/chat';
import { useHighlightStore } from '../../lib/highlightStore';

interface WorkspaceMember {
  publicId: string;
  name: string;
  email: string;
  role: string;
}

export function WidgetAgent({ clearSignal }: { clearSignal?: number }) {
  const { messages, isTyping, sendMessage, startTour, showTourStep, clearHistory } = useDominaAgent();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const store = useChatWidgetStore();
  const navigate = useNavigate();
  const ws = useCurrentWorkspace();
  const startDm = useStartDm();
  const flash = useHighlightStore((s) => s.flash);

  // Clear when the parent's clear button is pressed.
  const lastClear = useRef(clearSignal);
  useEffect(() => {
    if (clearSignal !== lastClear.current) {
      lastClear.current = clearSignal;
      clearHistory();
    }
  }, [clearSignal, clearHistory]);

  // Seed the first message from the home typewriter intro (consumed once).
  // '::tour::' is a sentinel that launches the guided tour instead of a question.
  useEffect(() => {
    const pending = store.consumePendingPrompt();
    if (pending === '::tour::') startTour();
    else if (pending) sendMessage(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep pinned to the newest message / typing indicator.
  useEffect(() => {
    const c = scrollRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, [messages, isTyping]);

  const wsMembers = useQuery({
    queryKey: ['workspace-members', ws?.publicId],
    queryFn: () => api<{ data: WorkspaceMember[] }>(`/workspaces/${ws!.publicId}/members`).then((r) => r.data),
    enabled: !!ws,
  });

  async function handleHuman() {
    const members = wsMembers.data;
    const human =
      members?.find((m) => m.role === 'OWNER') ?? members?.find((m) => m.role === 'ADMIN') ?? members?.[0];
    if (!human) {
      store.goToNewChat();
      return;
    }
    try {
      const room = await startDm.mutateAsync(human.publicId);
      store.goToThread(room.publicId);
    } catch {
      store.goToNewChat();
    }
  }

  function runAction(a: AgentAction) {
    if (a.to) {
      store.close();
      navigate(a.to);
      // Flash the page so it's clear what she's showing — even if you're
      // already on that page (the tour's "Show me"). Small delay lets a
      // cross-page navigation paint first.
      setTimeout(() => flash(), 220);
    } else if (a.href) {
      window.open(a.href, a.href.startsWith('mailto:') ? '_self' : '_blank');
    } else if (a.ask) {
      sendMessage(a.ask);
    } else if (a.kind === 'ai') {
      store.setDominaMode('ai');
    } else if (a.kind === 'human') {
      void handleHuman();
    } else if (a.kind === 'tour') {
      startTour();
    } else if (a.kind === 'tour-next') {
      showTourStep(a.stepIndex ?? 0);
    }
  }

  function handleSend() {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    sendMessage(text);
    inputRef.current?.focus();
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {/* Opening greeting (always shown) */}
        <AgentBubble
          message={{
            id: 'intro',
            role: 'assistant',
            content: "Howzit! 👋 I'm **Domina Agent** — your free in-app guide. Ask me anything about CNTRL, or let me show you around.",
            timestamp: '',
          }}
          onAction={runAction}
        />

        {/* Quick-start chips before the user has typed */}
        {messages.length === 0 && !isTyping && (
          <div className="mb-2 ml-9 flex flex-wrap gap-1.5">
            {QUICK_STARTS.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => runAction(q)}
                className="rounded-full border border-pink-200 bg-white px-2.5 py-1 text-[11px] font-medium text-pink-700 transition-colors hover:bg-pink-50"
              >
                {q.label}
              </button>
            ))}
          </div>
        )}

        {messages.map((m) => (
          <AgentBubble key={m.id} message={m} onAction={runAction} />
        ))}

        {isTyping && (
          <div className="mb-2 flex gap-2">
            <Avatar />
            <div className="rounded-xl rounded-bl-sm border border-pink-100/50 bg-gradient-to-br from-pink-50 to-indigo-50 px-4 py-3">
              <div className="flex gap-1">
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:0ms]" />
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:150ms]" />
                <span className="size-2 animate-bounce rounded-full bg-pink-400 [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compose */}
      <div className="border-t border-neutral-100 p-2">
        <div className="flex items-end gap-1.5">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask Domina Agent anything…"
            rows={2}
            className="flex-1 resize-none overflow-y-auto rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm leading-5 outline-none focus:border-pink-300 focus:ring-1 focus:ring-pink-100"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim()}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white transition-colors disabled:opacity-50"
            style={{ backgroundImage: brand.gradient.primary }}
          >
            <Send className="size-4" />
          </button>
        </div>
        <p className="mt-1 text-center text-[10px] text-neutral-400">Free · no AI credits used</p>
      </div>
    </div>
  );
}

function Avatar() {
  return (
    <div
      className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-white"
      style={{ backgroundImage: brand.gradient.primary }}
    >
      <Crown className="size-3.5" />
    </div>
  );
}

function AgentBubble({ message, onAction }: { message: AgentMessage; onAction: (a: AgentAction) => void }) {
  const isUser = message.role === 'user';
  const time = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  if (isUser) {
    return (
      <div className="mb-2 flex justify-end">
        <div className="max-w-[80%] space-y-0.5">
          <div className="rounded-xl rounded-br-sm bg-pink-500 px-3 py-2 text-sm text-white">{message.content}</div>
          {time && <span className="block text-right text-[10px] text-neutral-400">{time}</span>}
        </div>
      </div>
    );
  }

  // Escalation buttons appended when the Agent isn't confident.
  const extra: AgentAction[] =
    message.confident === false
      ? [
          { label: '🙋 Talk to a human', kind: 'human' },
          { label: '✉️ Email support', href: 'mailto:hello@iheartdigital.co.za' },
        ]
      : [];
  const actions = [...(message.actions ?? []), ...extra];

  return (
    <div className="mb-2 flex gap-2">
      <Avatar />
      <div className="max-w-[85%] space-y-1">
        <div className="rounded-xl rounded-bl-sm border border-pink-100/50 bg-gradient-to-br from-pink-50 to-indigo-50 px-3 py-2 text-sm leading-relaxed text-neutral-800">
          <MarkdownLite content={message.content} />
        </div>
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {actions.map((a, i) => {
              const isNext = a.kind === 'tour-next';
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onAction(a)}
                  className={cn(
                    'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                    isNext
                      ? 'text-white hover:opacity-90'
                      : 'border border-neutral-200 bg-white text-neutral-700 hover:border-pink-300 hover:bg-pink-50',
                  )}
                  style={isNext ? { backgroundImage: brand.gradient.primary } : undefined}
                >
                  {a.kind === 'human' && <User className="size-3" />}
                  {a.href?.startsWith('mailto:') && <Mail className="size-3" />}
                  {a.label}
                  {isNext && <ArrowRight className="size-3" />}
                </button>
              );
            })}
          </div>
        )}
        {time && <span className="block text-[10px] text-neutral-400">{time}</span>}
      </div>
    </div>
  );
}
